import React from 'react';
import { Box, Chip, Paper, Typography, Grid } from '@mui/material';
import {
  ShieldCheck as HealthAndSafety,
  Shield as Security,
  Globe as Public,
  Scale as Gavel,
  UserCheck as VerifiedUser,
  FileCheck as Policy
} from 'lucide-react';
import PolicyLayout from './PolicyLayout';

const ComplianceCard = ({ icon, title, status, children }) => (
  <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
      <Box sx={{ color: '#1976d2' }}>{icon}</Box>
      <Typography variant="h6" sx={{ fontWeight: 'bold', flex: 1 }}>
        {title}
      </Typography>
      <Chip
        label={status}
        size="small"
        color={status === 'Compliant' ? 'success' : status === 'In Progress' ? 'warning' : 'default'}
        variant="outlined"
      />
    </Box>
    <Box sx={{ color: '#4a5568', '& p': { mb: 1.5 }, '& ul': { pl: 2, mb: 0 }, '& li': { mb: 0.5 } }}>
      {children}
    </Box>
  </Paper>
);

const ComplianceDisclosures = () => {
  return (
    <PolicyLayout title="Compliance Disclosures" lastUpdated="January 20, 2026">
      <p>
        This page summarizes AppealBot's compliance posture across various regulatory frameworks.
        These disclosures are provided for informational purposes and do not constitute legal
        advice or certification guarantees.
      </p>

      <Box sx={{ my: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 2 }}>
          Compliance Status Overview
        </Typography>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
          <Chip icon={<HealthAndSafety size={16} />} label="HIPAA Compliant" color="success" />
          <Chip icon={<Security size={16} />} label="SOC 2 Aligned" color="primary" variant="outlined" />
          <Chip icon={<Gavel size={16} />} label="CCPA Compliant" color="success" />
        </Box>
      </Box>

      <Grid container spacing={3} sx={{ my: 2 }}>
        <Grid item xs={12}>
          <ComplianceCard
            icon={<HealthAndSafety size={28} />}
            title="HIPAA"
            status="Compliant"
          >
            <p>
              AppealBot is fully compliant with the Health Insurance Portability and
              Accountability Act (HIPAA) Privacy and Security Rules.
            </p>
            <p><strong>Our HIPAA compliance includes:</strong></p>
            <ul>
              <li>Business Associate Agreements (BAAs) with all covered entity customers</li>
              <li>Administrative, physical, and technical safeguards per the Security Rule</li>
              <li>Privacy policies and procedures aligned with the Privacy Rule</li>
              <li>Regular risk assessments and security audits</li>
              <li>Employee HIPAA training and awareness programs</li>
              <li>Breach notification procedures meeting regulatory requirements</li>
              <li>Minimum necessary standard for PHI access and use</li>
            </ul>
            <p>
              <strong>Note:</strong> AppealBot acts as a Business Associate when processing PHI
              on behalf of healthcare providers (Covered Entities). We are not a Covered Entity.
            </p>
          </ComplianceCard>
        </Grid>

        <Grid item xs={12}>
          <ComplianceCard
            icon={<Security size={28} />}
            title="SOC 2"
            status="In Progress"
          >
            <p>
              AppealBot is pursuing SOC 2 Type II certification with controls aligned to the
              Trust Services Criteria.
            </p>
            <p><strong>Current status:</strong></p>
            <ul>
              <li>Security controls are implemented and internally documented</li>
              <li>Policies align with SOC 2 Trust Services Criteria</li>
              <li>Third-party audit engagement planned</li>
              <li>Continuous monitoring and evidence collection in place</li>
            </ul>
            <p><strong>Trust Services Criteria coverage:</strong></p>
            <ul>
              <li><strong>Security:</strong> System protection against unauthorized access</li>
              <li><strong>Availability:</strong> System availability for operation and use</li>
              <li><strong>Confidentiality:</strong> Protection of confidential information</li>
              <li><strong>Processing Integrity:</strong> System processing completeness and accuracy</li>
              <li><strong>Privacy:</strong> Collection, use, and retention of personal information</li>
            </ul>
            <p>
              <strong>Disclaimer:</strong> AppealBot is not currently SOC 2 certified. We do not
              represent that we hold SOC 2 certification until explicitly stated upon completion
              of the audit process.
            </p>
          </ComplianceCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ComplianceCard
            icon={<Public size={28} />}
            title="GDPR"
            status="Not Applicable"
          >
            <p>
              AppealBot does not target or offer services to users in the European Union and
              does not intentionally process EU personal data.
            </p>
            <p><strong>If EU data is incidentally processed:</strong></p>
            <ul>
              <li>It is handled according to general data protection principles</li>
              <li>We apply reasonable security measures</li>
              <li>We do not transfer EU data to third countries without safeguards</li>
            </ul>
            <p>
              AppealBot does not represent itself as GDPR-compliant for EU-regulated
              processing operations.
            </p>
          </ComplianceCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ComplianceCard
            icon={<Gavel size={28} />}
            title="CCPA / CPRA"
            status="Compliant"
          >
            <p>
              AppealBot complies with the California Consumer Privacy Act (CCPA) and California
              Privacy Rights Act (CPRA).
            </p>
            <p><strong>Our practices:</strong></p>
            <ul>
              <li>We do not sell personal information</li>
              <li>We do not engage in cross-context behavioral advertising</li>
              <li>We honor California residents' privacy rights</li>
              <li>We provide required disclosures in our Privacy Policy</li>
              <li>We respond to verified consumer requests within 45 days</li>
            </ul>
          </ComplianceCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ComplianceCard
            icon={<VerifiedUser size={28} />}
            title="State Privacy Laws"
            status="Compliant"
          >
            <p>
              We monitor and comply with applicable state privacy laws, including:
            </p>
            <ul>
              <li>Virginia Consumer Data Protection Act (VCDPA)</li>
              <li>Colorado Privacy Act (CPA)</li>
              <li>Connecticut Data Privacy Act (CTDPA)</li>
              <li>State health information privacy laws</li>
            </ul>
            <p>
              Privacy rights and procedures are detailed in our Privacy Policy.
            </p>
          </ComplianceCard>
        </Grid>

        <Grid item xs={12} md={6}>
          <ComplianceCard
            icon={<Policy size={28} />}
            title="Industry Standards"
            status="Aligned"
          >
            <p>
              AppealBot aligns with industry best practices and standards:
            </p>
            <ul>
              <li>NIST Cybersecurity Framework</li>
              <li>CIS Controls</li>
              <li>OWASP security guidelines</li>
              <li>Healthcare industry security practices</li>
            </ul>
          </ComplianceCard>
        </Grid>
      </Grid>

      <h2>Customer Responsibilities</h2>
      <p>
        While AppealBot maintains compliance with applicable regulations, customers remain
        responsible for:
      </p>
      <ul>
        <li>Determining the regulatory applicability to their specific use of the Service</li>
        <li>Ensuring lawful and authorized submission of data</li>
        <li>Meeting payer-specific requirements and deadlines</li>
        <li>Complying with state-specific healthcare regulations</li>
        <li>Maintaining appropriate professional licenses and credentials</li>
        <li>Reviewing AI-generated content before submission to payers</li>
        <li>Implementing appropriate access controls within their organization</li>
      </ul>

      <h2>Compliance Documentation</h2>
      <p>
        Customers may request compliance documentation for due diligence purposes:
      </p>
      <ul>
        <li><strong>Security Questionnaires:</strong> We respond to standard security questionnaires (SIG, CAIQ)</li>
        <li><strong>BAA:</strong> Business Associate Agreement available upon request</li>
        <li><strong>Policies:</strong> Security and privacy policies available under NDA</li>
        <li><strong>Attestations:</strong> Available upon completion of SOC 2 audit</li>
      </ul>

      <h2>Compliance Updates</h2>
      <p>
        We continuously monitor regulatory developments and update our compliance posture
        accordingly. Material changes to our compliance status will be communicated through:
      </p>
      <ul>
        <li>Updates to this disclosure page</li>
        <li>Email notifications to affected customers</li>
        <li>In-app announcements where appropriate</li>
      </ul>

      <h2>Contact</h2>
      <p>
        For compliance inquiries or to request documentation:
      </p>
      <ul>
        <li>Email: compliance@appealbot.com</li>
        <li>Privacy Officer: privacy@appealbot.com</li>
        <li>Security Team: security@appealbot.com</li>
      </ul>
    </PolicyLayout>
  );
};

export default ComplianceDisclosures;
